/**
 * Shared zod schemas + types for the transcription provider surface
 * (M2 Phase 7). Voice messages from VK Teams are routed through a
 * pluggable transcription facade; this file pins the settings shape
 * before any provider implementation lands.
 */
import { z } from 'zod';
import { ruEnum } from './lib/zod-ru';

export const TranscriptionProviderName = z.enum(
  [
    'openai-whisper',
    'yandex-speechkit',
    // W3.12 — agent2 Mac mini mlx-whisper FastAPI behind reverse SSH tunnel.
    // Endpoint: POST /transcribe?language=ru, multipart `file` + Bearer auth.
    // Configured via `transcription.mlxBaseUrl` (URL, plain) +
    // `transcription.mlxBearerToken` (encrypted). Default URL inside docker:
    // http://host.docker.internal:9001 (autossh -R 9001 from agent2).
    'mlx-whisper-http',
  ],
  ruEnum('Выберите поставщика'),
);
export type TranscriptionProviderName = z.infer<typeof TranscriptionProviderName>;

export const TranscriptionSettingsSchema = z
  .object({
    // W3.23 — fresh-DB default is mlx-whisper-http. The legacy in-cluster
    // whisper.cpp sidecar was retired (RAM pressure) and its provider was
    // removed entirely (chore/remove-whisper-cpp-provider); the active
    // provider is agent2 mlx-whisper via reverse SSH tunnel, with the
    // prod aris-whisper (faster-whisper) container served over the same
    // `mlx-whisper-http` HTTP contract. Existing rows in SystemSetting
    // are untouched — this default only applies when
    // `transcription.provider` is absent from the DB (fresh install /
    // empty seed).
    provider: TranscriptionProviderName.default('mlx-whisper-http'),
    endpoint: z.string().trim().max(255).default(''),
    apiKey: z.string().max(1024).default(''),
    model: z.string().trim().max(120).default('medium'),
    enabled: z.coerce.boolean().default(false),
    /**
     * Upper bound on per-message audio duration. Voice messages above the
     * cap are rejected up-front (so we never spend STT budget on a stray
     * 30-minute clip). 90 s default matches RESEARCH §13.
     */
    maxAudioSeconds: z.coerce.number().int().min(5).max(600).default(90),
  })
  .strict();
export type TranscriptionSettings = z.infer<typeof TranscriptionSettingsSchema>;
